import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as DocumentFileIcon } from '../../assets/icons/document.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye-fill-icon.svg';
import { useAppBranding, currentBranding } from '../../config/branding';
import { branding } from '../../config/enum';
import { getTimeZones, getUserTimeZone, normalizeTimeZone } from '../../config/timezonesLabel';
import {
  NewProgressProps as NewProgressProperties,
  SingleFileUploadProgress,
} from '../../interface/file-upload-stream.interface';
import { AvailableStation } from '../../interface/station-management.interface';
import AmenitiesInput from '../../pages/station-management/components/AmenitiesInput';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchApprovedStations } from '../../redux/slice/ApprovedStationManagementSlice';
import {
  setUnUploadedFileData,
  updateFileUploadProgress,
} from '../../redux/slice/FileUploadStreamSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { fetchPendingStations } from '../../redux/slice/PendingStationManagementSlice';
import { fetchRejectedStations } from '../../redux/slice/RejectedStationManagementSlice';
import { convertTo12HourFormat } from '../../shared-functions/DataTime';
import { SanitizeFileName } from '../../shared-functions/StringFunctions';
import {
  checkEditFileSizeValidation,
  longTextValidation,
  stringValidation,
} from '../../shared-functions/Validation';
import chargingStation from '../../utils/api/station';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import CustomFileInput from '../custom-file-input/CustomFileInput';
import CustomInput from '../custom-input/CustomInput';
import CustomInputSwitch from '../custom-input-switch/CustomInputSwitch';
import CustomMediaUpload from '../custom-media-upload/CustomMediaUpload';
import CustomTable from '../custom-table/CustomTable';
import CustomTimePicker from '../custom-time-input/CustomTimePicker';
import FileUploadProgress from '../file-upload-progress/FileUploadProgress';
import ModalBox from '../modal-box/ModalBox';
import SelectField from '../select-field/SelectField';
import SelectLocation from '../select-location/SelectLocation';
import SuccessNotification from '../success-notification/SuccessNotification';
import TabToggle from '../tab-toggle/TabToggle';

interface StationSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  onEditModeChange?: (isEditMode: boolean) => void;
  onDeleteStation?: (stationId: string) => void;
}

interface ChargingPointProperties {
  chargingPointSn: string;
  connectorType: string; // use union if more types exist
  isDeviceOffline: boolean;
  _id: string;
  id: string;
}

interface DayOperatingHours {
  status: boolean;
  open: string;
  close: string;
}
interface OperatingHours {
  allDay: boolean;
  monday: DayOperatingHours;
  tuesday: DayOperatingHours;
  wednesday: DayOperatingHours;
  thursday: DayOperatingHours;
  friday: DayOperatingHours;
  saturday: DayOperatingHours;
  sunday: DayOperatingHours;
}

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

/**
 * View the file based on its type
 * @param param0
 * @returns
 */
function FilePreview({ file, extraCount = 0 }: { file: any; extraCount?: number }) {
  const { t } = useTranslation('tableSidepanel');

  let fileTypeView;

  if (file.fileType.includes('image')) {
    fileTypeView = <img src={file.signedUrl} alt="" className="selected-image" />;
  } else if (file.fileType.includes('video')) {
    fileTypeView = (
      <video controls className="selected-video">
        <source src={file.signedUrl} type={file.fileType} />
        <track src="path/to/captions.vtt" kind="captions" label="English captions" default />
        {t('browserNotVideoTag')}{' '}
      </video>
    );
  }

  return (
    <div className="single-selected-wrap">
      {fileTypeView}
      {extraCount && extraCount > 0 ? (
        <div className="extra-count-overlay">
          <span>+{extraCount}</span>
        </div>
      ) : null}
    </div>
  );
}
FilePreview.defaultProps = {
  extraCount: 0,
};

/**
 * Station side panel
 * @param properties
 * @returns
 */
function StationSidePanel(properties: StationSidePanelProperties) {
  const appBranding = useAppBranding();
  const { t, i18n } = useTranslation('tableSidepanel');
  const { fetchIdFrom, id, onEditModeChange, onDeleteStation } = properties;
  const { status: parameterStatus, page } = useParams();
  const status = parameterStatus || page;
  const dispatch = useAppDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState<any>({
    name: '',
    address: '',
    electricityContractType: '',
    StationLocation: '',
    editLandOwnerShipDocument: [],
    editEbReport: [],
    editMedias: [],
    removedMedias: [],
    amenities: [],
    timeZone: getUserTimeZone(),
    operatingHours: {
      allDay: true,
      monday: { isOpen: true, from: '00:00', to: '23:59' },
      tuesday: { isOpen: true, from: '00:00', to: '23:59' },
      wednesday: { isOpen: true, from: '00:00', to: '23:59' },
      thursday: { isOpen: true, from: '00:00', to: '23:59' },
      friday: { isOpen: true, from: '00:00', to: '23:59' },
      saturday: { isOpen: true, from: '00:00', to: '23:59' },
      sunday: { isOpen: true, from: '00:00', to: '23:59' },
    },
  });
  const [searchParameters, setSearchParameters] = useSearchParams();
  const activeDeviceId = searchParameters.get('deviceId');
  const [chargingStationData, setChargingStationData] = useState<AvailableStation>();
  const [chargingPointData, setChargingPointData] = useState<ChargingPointProperties[]>([]);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  // const [partLength, setPartLength] = useState<number>(1);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
    type: string;
    name: string;
  } | null>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<any>({
    title: '',
    content: null,
  });
  const [formStatus, setFormStatus] = useState({
    buttonLoader: false,
  });

  const { page: approvedPage, limit: approvedLimit } = useAppSelector(
    (state) => state.approvedStation,
  );
  const { page: rejectedPage, limit: rejectedLimit } = useAppSelector(
    (state) => state.rejectStation,
  );

  const navigate = useNavigate();

  const [openTab, setOpenTab] = useState<number>(0);

  /**
   * Change tab + update URL
   * @param tabLabel
   */
  const changeTabPages = (tabLabel: string) => {
    const index = [t('stationInformation'), t('operatingHours')].indexOf(tabLabel);
    if (index !== -1) {
      setOpenTab(index);
    }
  };

  const timeZoneOptions = React.useMemo(() => getTimeZones(), []);

  /**
   * Handle operating hours change
   * @param day
   * @param field
   * @param value
   */
  const handleOperatingHoursChange = (day: DayKey, field: 'isOpen' | 'from' | 'to', value: any) => {
    setEditData((previous: any) => {
      if (!previous) return previous;
      return {
        ...previous,
        operatingHours: {
          ...previous.operatingHours!,
          [day]: {
            ...(previous.operatingHours as any)?.[day],
            [field]: value,
          },
        },
      };
    });
  };

  /**
   * Handle All Day toggle
   * @param name
   * @param value
   */
  const handleAllDayToggle = (name: string, value: boolean) => {
    setEditData((previous: any) => {
      if (!previous) return previous;
      const updatedOperatingHours = { ...previous.operatingHours! };
      updatedOperatingHours.allDay = value;

      if (value) {
        const fullDay = { isOpen: true, from: '00:00', to: '23:59' };
        updatedOperatingHours.monday = fullDay;
        updatedOperatingHours.tuesday = fullDay;
        updatedOperatingHours.wednesday = fullDay;
        updatedOperatingHours.thursday = fullDay;
        updatedOperatingHours.friday = fullDay;
        updatedOperatingHours.saturday = fullDay;
        updatedOperatingHours.sunday = fullDay;
      } else {
        const closedDay = { isOpen: false, from: '00:00', to: '23:59' };
        updatedOperatingHours.monday = closedDay;
        updatedOperatingHours.tuesday = closedDay;
        updatedOperatingHours.wednesday = closedDay;
        updatedOperatingHours.thursday = closedDay;
        updatedOperatingHours.friday = closedDay;
        updatedOperatingHours.saturday = closedDay;
        updatedOperatingHours.sunday = closedDay;
      }

      return {
        ...previous,
        operatingHours: updatedOperatingHours as OperatingHours,
      };
    });
  };

  /**
   * Handle Day Toggle
   * @param day
   * @param isOpen
   */
  const handleDayToggle = (day: DayKey, isOpen: boolean) => {
    handleOperatingHoursChange(day, 'isOpen', isOpen);
  };

  /**
   * Handle open device side panel
   * @param _
   * @param row
   */
  const handleOpenDeviceSidePanel = (_: string, row: ChargingPointProperties) => {
    if (!row?._id) return;

    const newParameters = new URLSearchParams(searchParameters);
    if (newParameters.has('stationId')) {
      newParameters.delete('stationId');
    }

    newParameters.set('deviceId', row._id);
    setSearchParameters(newParameters);
  };

  /**
   * Render operating hours in a 3-column grid
   */
  const renderOperatingHoursGrid = (hours: OperatingHours | undefined) => {
    if (!hours) return <p className="basic-info-value">N/A</p>;

    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ] as const;

    return (
      <div className="opening-hours-grid">
        {days.map((day) => {
          const dayData = hours[day];
          console.log(dayData, 'dfghjklkjhgfdftyuikmn');
          return (
            <div key={day} className="day-grid-item">
              <p className="day-label">{t(day)}</p>
              {dayData?.status ? (
                <div className="time-range">
                  <span>
                    {dayData.open ? convertTo12HourFormat(dayData.open) : 'N/A'} {' -'}
                  </span>
                  <span>{dayData.close ? convertTo12HourFormat(dayData.close) : 'N/A'}</span>
                </div>
              ) : (
                <p className="closed-label">{t('closed')}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Handle view document
   */
  const handleViewDocument = (file: any) => {
    if (file?.signedUrl) {
      setSelectedDocument({
        url: file.signedUrl,
        type: file.fileType || 'application/pdf', // Fallback or logic
        name: file.fileName || 'Document',
      });
      setIsDocumentModalOpen(true);
    }
  };

  const basicInfoDetails = [
    {
      label: t('electricityType'),
      value: 'electricityContractType' as keyof AvailableStation,
    },
    {
      label: t('timeZone'),
      value: 'time_zone' as keyof AvailableStation,
    },
    {
      label: t('operatingHours'),
      value: 'operatingHours' as keyof AvailableStation,
      /**
       * Function to get custom data
       * @param row
       * @returns
       */
      customData: (row: AvailableStation) => renderOperatingHoursGrid(row?.operatingHours),
    },
    {
      label: t('addressLabel'),
      value: 'address' as keyof AvailableStation,
    },
  ];

  const amenitiesDetails = [
    {
      name: t('gasStation'),
      key: 'Gas Station',
      svgIconActive: <appBranding.icons.GasStationActive />,
    },
    {
      name: t('wifi'),
      key: 'Free Wifi',
      svgIconActive: <appBranding.icons.WifiActive />,
    },
    {
      name: t('restroom'),
      key: 'Restroom',
      svgIconActive: <appBranding.icons.RestroomActive />,
    },
    {
      name: t('lounge'),
      key: 'Lounge',
      svgIconActive: <appBranding.icons.LoungeActive />,
    },
    {
      name: t('cafe'),
      key: 'Cafe',
      svgIconActive: <appBranding.icons.CaffeeActive />,
    },
    {
      name: t('shopping'),
      key: 'Shopping',
      svgIconActive: <appBranding.icons.ShoppingActive />,
    },
    {
      name: t('restaurant'),
      key: 'Restaurant',
      svgIconActive: <appBranding.icons.RestaurantActive />,
    },
    {
      name: t('relaxingArea'),
      key: 'Relaxing Area',
      svgIconActive: <appBranding.icons.RelaxingActive />,
    },
    // {
    //   name: 'Locker',
    //   key: 'Locker',
    //   svgIconActive: <appBranding.icons.LockerActive />,
    // },
    // {
    //   name: 'Work Shop',
    //   key: 'Work Shop',
    //   svgIconActive: <appBranding.icons.WorkshopActive />,
    // },
    {
      name: t('carWash'),
      key: 'Car Wash',
      svgIconActive: <appBranding.icons.CarwashActive />,
    },
  ];

  const column = [
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ChargingPointProperties,
      sortAllow: false,
      /**
       * Render device name
       * @param row
       */
      customData: (row: ChargingPointProperties) => (
        <p className="table-row-each-data hyperlink">{row.chargingPointSn}</p>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'isDeviceOffline' as keyof ChargingPointProperties,
      sortAllow: false,
      /**
       * Function
       * @param row
       * @returns
       */
      customData: (row: ChargingPointProperties) => (
        <p>{row.isDeviceOffline ? t('offline') : t('online')}</p>
      ),
    },
    {
      label: t('connectorType'),
      accessorKey: 'connectorType' as keyof ChargingPointProperties,
      sortAllow: false,
    },
  ];

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchStationDetails = async (stationId: string) => {
    setIsLoader(true);
    chargingStation
      .fetchChargingStationByStationId(stationId)
      .then((response) => {
        setChargingStationData(response?.data);
        setIsLoader(false);
      })
      .catch((error) => {
        setIsLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchDeviceDetails = async (stationId: string) => {
    chargingStation
      .fetchChargingPointByStationId(stationId)
      .then((response) => {
        setChargingPointData(response.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to render document chip
   * @param param0 - DocumentChipProps
   * @returns JSX.Element
   */
  function DocumentChip({
    label,
    file = undefined,
    onClick,
  }: {
    label: string;
    file?: any;
    onClick: () => void;
  }) {
    return (
      <div className="document-chip-wrap">
        <span className="doc-label">{label === 'ebReports' ? t('ebReports') : label}</span>
        <div className="doc-file-box">
          <div className="doc-icon-name">
            <DocumentFileIcon />
            <span className="file-name">{file ? file.fileName : t('noFile')}</span>
          </div>
          {file && (
            <div className="doc-view-icon" role="presentation" onClick={onClick}>
              <EyeIcon />
            </div>
          )}
        </div>
      </div>
    );
  }
  DocumentChip.defaultProps = {
    file: undefined,
  };

  const electricityContractType = [
    {
      label: t('standardResidential'),
      value: 'standard-residential',
    },
    {
      label: t('commercial'),
      value: 'commercial',
    },
    {
      label: t('industrial'),
      value: 'industrial',
    },
    {
      label: t('evChargingSpecificContracts'),
      value: 'ev-charging-specific-contracts',
    },
  ];

  /**
   * Handle close document modal
   */
  const handleCloseDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
  };

  /**
   * Handle Input Change
   */
  const handleInputChange = (name: string, value: string | number | any) => {
    if (name === 'governmentId') {
      setEditData((previous: any) => ({
        ...previous,
        [name]: value,
        governmentIdOld: [],
      }));
    } else {
      setEditData((previous: any) => ({
        ...previous,
        [name]: value,
      }));
    }
  };

  /**
   * Handle lat & long change
   * @param changeValue
   */
  const handleChangeLatLong = (latitude: number, longitude: number) => {
    setEditData((previousValue: any) => ({
      ...previousValue,
      latitude,
      longitude,
    }));
  };

  /**
   * Function to remove media
   * @param name - name of the media
   * @param index - index of the media
   * @param url - url of the media
   * @param type - type of the media
   */
  const handleRemoveMedias = (
    name: string,
    index: number,
    url: string,
    type: 'newFile' | 'existingFile',
  ) => {
    if (type === 'newFile') {
      setEditData((previous: any) => ({
        ...previous,
        editMedias: previous.editMedias.filter((_: any, index_: number) => index_ !== index),
      }));
    }

    if (type === 'existingFile') {
      setEditData((previous: any) => ({
        ...previous,
        medias: (chargingStationData?.medias || []).filter((m: any) => m.url !== url),
        removedMedias: [...(previous.removedMedias || []), url],
      }));
      setChargingStationData((previous: any) => ({
        ...previous,
        medias: (previous?.medias || []).filter((m: any) => m.url !== url),
      }));
    }
  };

  /**
   * Toggle edit mode
   */
  const toggleEditMode = () => {
    if (isEdit) {
      setIsEdit(false);
      setEditData({});
      onEditModeChange?.(false);
      return;
    }

    setEditData({
      id: chargingStationData?._id, // VERY IMPORTANT
      name: chargingStationData?.name || '',
      address: chargingStationData?.address || '',
      electricityContractType: chargingStationData?.electricityContractType || '',
      StationLocation: chargingStationData?.stationLocation || '',

      latitude: chargingStationData?.latitude || '',
      longitude: chargingStationData?.longitude || '',
      electricityCostByUtilityProvider: chargingStationData?.electricityCostByUtilityProvider || 0,
      isGuestChargingExclusive: chargingStationData?.isGuestChargingExclusive || false,

      editLandOwnerShipDocument: [],
      editEbReport: [],
      editMedias: [],
      removedMedias: [],
      removedLandOwnerShipDocument: [],
      removedEbReport: [],
      amenities: chargingStationData?.amenities || [],
      timeZone: normalizeTimeZone(chargingStationData?.time_zone || getUserTimeZone()),
      operatingHours: chargingStationData?.operatingHours
        ? {
            allDay: chargingStationData.operatingHours.allDay || false,
            monday: {
              isOpen: chargingStationData.operatingHours.monday?.status === 'open',
              from: chargingStationData.operatingHours.monday?.open || '00:00',
              to: chargingStationData.operatingHours.monday?.close || '23:59',
            },
            tuesday: {
              isOpen: chargingStationData.operatingHours.tuesday?.status === 'open',
              from: chargingStationData.operatingHours.tuesday?.open || '00:00',
              to: chargingStationData.operatingHours.tuesday?.close || '23:59',
            },
            wednesday: {
              isOpen: chargingStationData.operatingHours.wednesday?.status === 'open',
              from: chargingStationData.operatingHours.wednesday?.open || '00:00',
              to: chargingStationData.operatingHours.wednesday?.close || '23:59',
            },
            thursday: {
              isOpen: chargingStationData.operatingHours.thursday?.status === 'open',
              from: chargingStationData.operatingHours.thursday?.open || '00:00',
              to: chargingStationData.operatingHours.thursday?.close || '23:59',
            },
            friday: {
              isOpen: chargingStationData.operatingHours.friday?.status === 'open',
              from: chargingStationData.operatingHours.friday?.open || '00:00',
              to: chargingStationData.operatingHours.friday?.close || '23:59',
            },
            saturday: {
              isOpen: chargingStationData.operatingHours.saturday?.status === 'open',
              from: chargingStationData.operatingHours.saturday?.open || '00:00',
              to: chargingStationData.operatingHours.saturday?.close || '23:59',
            },
            sunday: {
              isOpen: chargingStationData.operatingHours.sunday?.status === 'open',
              from: chargingStationData.operatingHours.sunday?.open || '00:00',
              to: chargingStationData.operatingHours.sunday?.close || '23:59',
            },
          }
        : {
            allDay: true,
            monday: { isOpen: true, from: '00:00', to: '23:59' },
            tuesday: { isOpen: true, from: '00:00', to: '23:59' },
            wednesday: { isOpen: true, from: '00:00', to: '23:59' },
            thursday: { isOpen: true, from: '00:00', to: '23:59' },
            friday: { isOpen: true, from: '00:00', to: '23:59' },
            saturday: { isOpen: true, from: '00:00', to: '23:59' },
            sunday: { isOpen: true, from: '00:00', to: '23:59' },
          },
    });

    setIsEdit(true);
    onEditModeChange?.(true);
  };

  /**
   * Function to validate media files
   * @returns
   */
  const validateMedia = () => {
    if (currentBranding === branding.VOLTIE) return true;

    const existingMediaCount = chargingStationData?.medias?.length || 0;
    const newMediaCount = editData.editMedias?.length || 0;

    // allow existing media
    if (existingMediaCount > 0 && newMediaCount === 0) {
      return true;
    }

    // limit only for new uploads
    if (newMediaCount > 10) return false;

    if (!checkEditFileSizeValidation(editData.editMedias, 5, 10)) return false;

    if (existingMediaCount + newMediaCount === 0) return false;

    return true;
  };

  /**
   * Handle remove existing file
   * @param name - File name
   * @param url - File url
   * @param index - File index
   * @param type - File type
   */
  const handleRemoveExistingFile = (
    name: string,
    url: string,
    index: number,
    type: 'newFile' | 'existingFile',
  ) => {
    if (type === 'newFile') {
      setEditData((previous: any) => ({
        ...previous,
        [name]: (previous[name] || []).filter((_: any, index_: number) => index_ !== index),
      }));
    } else if (type === 'existingFile') {
      if (name === 'landOwnerShipDocument') {
        setEditData((previous: any) => ({
          ...previous,
          removedLandOwnerShipDocument: [...(previous.removedLandOwnerShipDocument || []), url],
        }));
        setChargingStationData((previous: any) => ({
          ...previous,
          landOwnerShipDocument: (previous?.landOwnerShipDocument || []).filter(
            (u: any) => u?.url !== url,
          ),
        }));
      } else if (name === 'ebReport') {
        setEditData((previous: any) => ({
          ...previous,
          removedEbReport: [...(previous.removedEbReport || []), url],
        }));
        setChargingStationData((previous: any) => ({
          ...previous,
          ebReport: (previous?.ebReport || []).filter((u: any) => u?.url !== url),
        }));
      }
    }
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = (fetchUpdatedData: boolean = false) => {
    setEditData({
      id: '',
      _id: '',
      name: '',
      landOwnerShipDocument: null,
      editLandOwnerShipDocument: [],
      removedLandOwnerShipDocument: [],
      ebReport: null,
      editEbReport: [],
      removedEbReport: [],
      electricityContractType: '',
      address: '',
      latitude: null,
      longitude: null,
      medias: null,
      editMedias: [],
      removedMedias: [],
      amenities: [],
      electricityCostByUtilityProvider: null,
      isGuestChargingExclusive: false,
    });
    setModalBoxStatus('close');

    navigate(`../${status}`);
    if (status === 'approved' && fetchUpdatedData) {
      dispatch(fetchApprovedStations({ page: approvedPage, limit: approvedLimit }));
    } else if (status === 'rejected' && fetchUpdatedData) {
      dispatch(fetchRejectedStations({ page: rejectedPage, limit: rejectedLimit }));
    } else if (status === 'pending' && fetchUpdatedData) {
      dispatch(fetchPendingStations({ page: approvedPage, limit: approvedLimit }));
    }
  };

  /**
   * Function to get and handle file upload progress
   */
  const handleUploadProgress = (progress: NewProgressProperties) => {
    if (progress?.fileName && progress?.percentage) {
      dispatch(updateFileUploadProgress(progress));
    }
  };

  /**
   * Update station API call
   */
  const handleSubmitStationDetails = () => {
    if (
      stringValidation(1, 100, editData.name) &&
      (currentBranding === branding.VOLTIE ||
        checkEditFileSizeValidation(editData.editLandOwnerShipDocument, 5, 1)) &&
      (currentBranding === branding.VOLTIE ||
        (chargingStationData?.landOwnerShipDocument?.length || 0) +
          (editData.editLandOwnerShipDocument?.length || 0) ===
          1) &&
      (currentBranding === branding.VOLTIE ||
        checkEditFileSizeValidation(editData.editEbReport, 5, 1)) &&
      (currentBranding === branding.VOLTIE ||
        (chargingStationData?.ebReport?.length || 0) + (editData.editEbReport?.length || 0) ===
          1) &&
      editData.electricityContractType &&
      longTextValidation(500, editData.address) &&
      editData.latitude &&
      editData.longitude &&
      validateMedia() &&
      editData.electricityCostByUtilityProvider !== null &&
      editData.electricityCostByUtilityProvider >= 0
      // stationDetails.amenities?.length > 0
    ) {
      setFormStatus({
        buttonLoader: true,
      });
      const formData = new FormData();

      // Append simple fields
      formData.append('name', editData.name);
      formData.append('electricityContractType', editData.electricityContractType);

      formData.append('address', editData.address);
      formData.append(
        'electricityCostByUtilityProvider',
        editData.electricityCostByUtilityProvider.toString(),
      );
      formData.append('isGuestChargingExclusive', editData.isGuestChargingExclusive.toString());
      formData.append('latitude', editData.latitude ? editData.latitude.toString() : '');
      formData.append('longitude', editData.longitude ? editData.longitude.toString() : '');

      // Append operatingHours and timezone
      if (editData.operatingHours) {
        /** operating hours */
        const days = [
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
          'sunday',
        ] as const;

        days.forEach((day) => {
          const dayData = editData.operatingHours?.[day];

          if (dayData?.isOpen) {
            formData.append(`operatingHours[${day}][open]`, dayData.from);
            formData.append(`operatingHours[${day}][close]`, dayData.to);
            formData.append(`operatingHours[${day}][status]`, 'open');
          } else {
            formData.append(`operatingHours[${day}][status]`, 'closed');
          }
        });
      }

      if (editData.timeZone) {
        formData.append('time_zone', editData.timeZone || '');
      }

      // Append files for landOwnerShipDocument if it's an array
      if (Array.isArray(editData.editLandOwnerShipDocument) && status !== 'approved') {
        editData.editLandOwnerShipDocument.forEach((file: any) => {
          formData.append(`landOwnerShipDocument`, file);
        });
      }

      // Append files for ebReport if it's an array
      if (Array.isArray(editData.editEbReport) && status !== 'approved') {
        editData.editEbReport.forEach((file: any) => {
          formData.append(`ebReport`, file);
        });
      }

      // Append multiple files for medias (if it's an array)
      if (editData.editMedias && Array.isArray(editData.editMedias)) {
        editData.editMedias.forEach((media: any) => {
          formData.append(`medias`, media);
        });
      }

      // Append multiple amenities
      if (editData.amenities && Array.isArray(editData.amenities)) {
        editData.amenities.forEach((amenity: string, index: number) => {
          formData.append(`amenities[${index}]`, amenity);
        });
      }

      // RemovedURLs
      let removedURL: string[] = [...(editData.removedMedias || [])];

      if (status !== 'approved' && editData.removedEbReport) {
        removedURL = [...removedURL, ...editData.removedEbReport];
      } else if (status === 'approved' && editData.removedEbReport.length > 0) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: 400,
              message: t('ebReportEditNotAllowed'),
            },
          }),
        );
        setFormStatus({
          buttonLoader: false,
        });
        return;
      }

      if (status !== 'approved' && editData.removedLandOwnerShipDocument) {
        removedURL = [...removedURL, ...editData.removedLandOwnerShipDocument];
      } else if (status === 'approved' && editData.removedLandOwnerShipDocument.length > 0) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: 400,
              message: t('landDocEditNotAllowed'),
            },
          }),
        );
        setFormStatus({
          buttonLoader: false,
        });
        return;
      }

      if (removedURL.length > 0) {
        if (removedURL?.length) {
          removedURL.forEach((url, index) => {
            formData.append(`removedUrls[${index}]`, url);
          });
        }
      }

      const binaryFileData: SingleFileUploadProgress[] = [
        ...(editData.editLandOwnerShipDocument || []),
        ...(editData.editEbReport || []),
        ...(editData.editMedias || []),
      ]?.map((fileData) => ({
        fileName: SanitizeFileName(fileData.name),
        progress: 0,
        status: 'pending',
      }));

      if (binaryFileData?.length) {
        dispatch(setUnUploadedFileData(binaryFileData));
        setModalBoxStatus('open');
        setModalBoxDetail({
          title:
            binaryFileData?.length === 1
              ? t('uploadingFile')
              : `${binaryFileData?.length} ${t('uploadingFiles')}`,
          content: <FileUploadProgress />,
          notificationModal: false,
        });
      }

      chargingStation
        .editStation(editData.id, formData, handleUploadProgress)
        .then(() => {
          setModalBoxStatus('open');
          setFormStatus({
            buttonLoader: false,
          });
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('stationEditedSuccessfully')}
                description={t('stationEditedDescription')}
                notificationCloseTimer={3000}
                onNotificationClose={() => handleNavigateBack(true)}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setModalBoxStatus('close');
          setFormStatus({
            buttonLoader: false,
          });
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    } else {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 400,
            message: t('fillRequiredFields'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('stationId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }

    handleFetchStationDetails(stationId);
    handleFetchDeviceDetails(stationId);
  }, [searchParameters, id]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="station-side-panel-wrap" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        header={false}
      />
      <ModalBox
        title={selectedDocument?.name || t('documentPreview')}
        open={isDocumentModalOpen}
        closeModal={handleCloseDocumentModal}
        header
        position="center"
        content={
          <div className="document-preview-container">
            {(() => {
              if (!selectedDocument?.url)
                return <p className="document-preview-text">{t('noDocumentToDisplay')}</p>;

              if (selectedDocument.type.includes('image')) {
                return (
                  <img
                    src={selectedDocument.url}
                    alt="Document"
                    className="document-preview-image"
                  />
                );
              }

              return (
                <iframe
                  src={selectedDocument.url}
                  title="Document Viewer"
                  className="document-preview-iframe"
                />
              );
            })()}
          </div>
        }
      />
      <div className="station-details-header">
        <p className="title">{isEdit ? t('editStationDetails') : t('stationDetails')}</p>

        {!isEdit && status !== 'pending' && (
          <div className="action-buttons">
            <EditIcon className="icon edit" onClick={toggleEditMode} />
            {onDeleteStation && (
              <DeleteIcon
                className="icon delete"
                onClick={() => onDeleteStation(chargingStationData?._id || '')}
              />
            )}
          </div>
        )}
      </div>
      {/* ===================== STATION INFO (TOP) ===================== */}
      {!isEdit && (
        <div className="station-data-wrapper">
          <div className="station-content-wrap">
            <div className="station-content-header">
              <h2 className="station-name">{chargingStationData?.name ?? 'N/A'}</h2>
              {!isEdit && (
                <p className="station-address">
                  {chargingStationData?.facilityTagData?.[0]?.facilityTagName ?? 'N/A'}
                </p>
              )}
            </div>
            <div className="divider" />
            <div className="station-basic-details">
              <p className="title">{t('basicInformation')}</p>

              {basicInfoDetails.map((item) => (
                <div key={item.label} className="basic-info-item">
                  <p className="basic-info-label">{item.label}:</p>
                  {item.customData ? (
                    item.customData(chargingStationData!)
                  ) : (
                    <p className="basic-info-value">
                      {String(chargingStationData?.[item.value] ?? 'N/A')}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="divider" />
            <div className="station-basic-details">
              <p className="title">{t('documents')}</p>

              <div className="documents-row">
                <DocumentChip
                  label={t('landDocument')}
                  file={chargingStationData?.landOwnerShipDocument?.[0]}
                  onClick={() =>
                    handleViewDocument(chargingStationData?.landOwnerShipDocument?.[0])
                  }
                />
                <DocumentChip
                  label={t('ebReports')}
                  file={chargingStationData?.ebReport?.[0]}
                  onClick={() => handleViewDocument(chargingStationData?.ebReport?.[0])}
                />
              </div>
            </div>
            {status === 'rejected' && !isEdit && (
              <div className="station-status">
                <p className="title">{t('stationVerification')}</p>
                <div className="reason">
                  <p className="reason-text">
                    {t('rejectedReason')}:{' '}
                    <span>{chargingStationData?.submission?.reason || 'N/A'}</span>
                  </p>
                </div>
              </div>
            )}
          </div>
          {/* Media */}
          <div className="section-block media-block">
            <p className="title">{t('mediaAmenities')}</p>

            {/* VIEW MODE MEDIA */}
            <div className="media-grid-view">
              {chargingStationData?.medias?.slice(0, 4).map((media, index) => {
                const remaining = (chargingStationData?.medias?.length || 0) - 4;
                return (
                  <FilePreview
                    key={media.url}
                    file={media}
                    extraCount={index === 3 ? remaining : 0}
                  />
                );
              })}
            </div>

            {/* AMENITIES VIEW */}
            {chargingStationData?.amenities?.length ? (
              <>
                <div className="divider" />
                <div className="amenities-container">
                  <p className="title">{t('amenities')}</p>

                  <div className="amenities-list">
                    {chargingStationData?.amenities?.map((each: any) => {
                      const amenityToShow =
                        amenitiesDetails.find((a) => a.key === each) || amenitiesDetails[0];

                      return (
                        <div className="single-amenity-item" key={each}>
                          <div className="icon-box">{amenityToShow.svgIconActive}</div>
                          <span>{amenityToShow.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {isEdit && (
        <div className="add-station-tabs">
          <TabToggle
            tabs={[t('stationInformation'), t('operatingHours')]}
            defaultTab="stationInformation"
            onChange={(tab) => changeTabPages(tab)}
          />
        </div>
      )}
      {isEdit && (
        <div className="station-data-wrapper">
          {openTab === 0 && (
            <>
              <div className="station-content-wrap">
                <div className="station-content-header">
                  <div className="header-edit-row">
                    <p className="header-label">{t('stationName')}:</p>

                    <div className="header-input">
                      <CustomInput
                        type="text"
                        name="name"
                        placeholder={t('stationName')}
                        value={editData.name}
                        onChangeValue={handleInputChange}
                        valueRequired
                        error={!editData.name}
                        errorMessage={t('stationNameRequired')}
                      />
                    </div>
                  </div>
                </div>
                <div className="divider" />
                <div className="station-basic-details">
                  <p className="title">{t('basicInformation')}</p>

                  {/* Electricity */}
                  <div className="basic-info-item">
                    <p className="basic-info-label">{t('electricityType')}:</p>
                    <div className="basic-info-input">
                      <SelectField
                        name="electricityContractType"
                        placeholder={t('chooseElectricityContractType')}
                        selectedValue={editData.electricityContractType}
                        onChangeValue={handleInputChange}
                        options={electricityContractType}
                        valueRequired
                        error={!editData.electricityContractType}
                        errorMessage={t('electricityContractTypeRequired')}
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="basic-info-item">
                    <p className="basic-info-label">{t('addressLabel')}:</p>
                    <div className="basic-info-input">
                      <CustomInput
                        type="text"
                        name="address"
                        placeholder={t('address')}
                        value={editData.address}
                        onChangeValue={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Usag Cost */}
                  <div className="basic-info-item">
                    <p className="basic-info-label">{t('usageCost')}:</p>
                    <div className="basic-info-input">
                      <CustomInput
                        type="text"
                        name="electricityCostByUtilityProvider"
                        placeholder={t('usageCost')}
                        value={editData.electricityCostByUtilityProvider}
                        onChangeValue={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Location */}
                  <div className="basic-info-item">
                    <p className="basic-info-label">{t('location')}:</p>
                    <SelectLocation
                      latitude={editData.latitude}
                      longitude={editData.longitude}
                      onChangeValue={handleChangeLatLong}
                      valueRequired
                      error={!(editData.latitude && editData.longitude)}
                      errorMessage={t('latLongRequiredError')}
                    />
                  </div>
                </div>
                <div className="divider" />
                <div className="station-basic-details">
                  <p className="title">{t('documents')}</p>

                  {/* Land document */}
                  <div className="documents-edit-grid">
                    {/* LEFT */}
                    <div className="doc-col">
                      <p className="doc-label">{t('landDocument')}:</p>

                      <CustomFileInput
                        name="editLandOwnerShipDocument"
                        placeholder={t('uploadPdfJpeg')}
                        onChangeValue={handleInputChange}
                        acceptedFileType="image/*, application/pdf"
                        selectedFiles={editData?.editLandOwnerShipDocument}
                        existingName="landOwnerShipDocument"
                        existingFiles={chargingStationData?.landOwnerShipDocument}
                        unselectHandler={handleRemoveExistingFile}
                        error={
                          !(
                            (currentBranding === branding.VOLTIE ||
                              checkEditFileSizeValidation(
                                editData.editLandOwnerShipDocument || [],
                                5,
                                1,
                              )) &&
                            (currentBranding === branding.VOLTIE ||
                              (chargingStationData?.landOwnerShipDocument?.length || 0) +
                                (editData.editLandOwnerShipDocument?.length || 0) ===
                                1)
                          )
                        }
                        valueRequired={currentBranding !== branding.VOLTIE}
                        errorMessage={t('documentRequired')}
                        readOnly={
                          currentBranding !== branding.VOLTIE ? status === 'approved' : false
                        }
                      />
                    </div>

                    {/* RIGHT */}
                    <div className="doc-col">
                      <p className="doc-label">{t('ebReports')}:</p>

                      <CustomFileInput
                        name="editEbReport"
                        placeholder={t('uploadPdf')}
                        onChangeValue={handleInputChange}
                        acceptedFileType="application/pdf"
                        selectedFiles={editData?.editEbReport}
                        existingName="ebReport"
                        existingFiles={chargingStationData?.ebReport}
                        unselectHandler={handleRemoveExistingFile}
                        valueRequired={currentBranding !== branding.VOLTIE}
                        error={
                          !(
                            (currentBranding === branding.VOLTIE ||
                              checkEditFileSizeValidation(editData.editEbReport || [], 5, 1)) &&
                            (currentBranding === branding.VOLTIE ||
                              (chargingStationData?.ebReport?.length || 0) +
                                (editData.editEbReport?.length || 0) ===
                                1)
                          )
                        }
                        errorMessage={t('documentRequired')}
                        readOnly={
                          currentBranding !== branding.VOLTIE ? status === 'approved' : false
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="section-block media-block">
                <p className="title">{t('mediaAmenities')}</p>
                <CustomMediaUpload
                  name="editMedias"
                  selectedFiles={editData.editMedias}
                  existingName="medias"
                  existingFiles={chargingStationData?.medias}
                  onChangeValue={handleInputChange}
                  unselectHandler={handleRemoveMedias}
                  acceptedFileType="image/* video/*"
                  placeholder={t('uploadMedia')}
                  error={
                    currentBranding !== branding.VOLTIE &&
                    ((chargingStationData?.medias?.length || 0) +
                      (editData.editMedias?.length || 0) ===
                      0 ||
                      !checkEditFileSizeValidation(editData.editMedias, 5, 10))
                  }
                />
                <AmenitiesInput
                  selectedAmenities={editData.amenities}
                  handleStationDetailChange={(name: string, value: any) =>
                    setEditData((previous: any) => ({ ...previous, [name]: value }))
                  }
                />
              </div>
            </>
          )}
          {openTab === 1 && (
            <div className="operating-hours-container">
              <div className="timezone-section">
                <SelectField
                  inputLabel={t('timeZone')}
                  name="timeZone"
                  placeholder={t('selectTimeZone')}
                  selectedValue={editData.timeZone || ''}
                  onChangeValue={(name, value) =>
                    setEditData((previous: any) => ({
                      ...previous,
                      [name]: normalizeTimeZone(value as string),
                    }))
                  }
                  options={timeZoneOptions}
                />
              </div>

              <div className="opening-hours-section">
                <div className="section-header">
                  <p className="section-title">{t('stationOpeningHours')}</p>
                  <div className="swith-button">
                    <p>{t('allDay')}</p>
                    <div>
                      <CustomInputSwitch
                        inputLabel=""
                        name="allDay"
                        value={editData.operatingHours?.allDay}
                        onChangeValue={handleAllDayToggle}
                      />{' '}
                    </div>
                  </div>
                </div>

                <div className="days-config-layout">
                  {[
                    'monday',
                    'tuesday',
                    'wednesday',
                    'thursday',
                    'friday',
                    'saturday',
                    'sunday',
                  ].map((day) => {
                    const dayData = editData.operatingHours?.[day as keyof OperatingHours] as any;
                    return (
                      <div className="day-row" key={day}>
                        <div className="day-toggle-name">
                          <CustomInputSwitch
                            name={day}
                            value={dayData?.isOpen}
                            onChangeValue={(name, value) => handleDayToggle(name as DayKey, value)}
                          />
                          <p className="day-name">{t(day)}</p>
                        </div>

                        {dayData?.isOpen ? (
                          <div className="day-times-wrap">
                            <div className="day-times">
                              <div className="time-field-wrap">
                                <CustomTimePicker
                                  innerLabel={t('from')}
                                  name={`${day}-from`}
                                  value={dayData?.from}
                                  onChangeValue={(_n, v) =>
                                    handleOperatingHoursChange(day as DayKey, 'from', v)
                                  }
                                />
                              </div>
                              <div className="time-field-wrap">
                                <CustomTimePicker
                                  innerLabel={t('to')}
                                  name={`${day}-to`}
                                  value={dayData?.to}
                                  onChangeValue={(_n, v) =>
                                    handleOperatingHoursChange(day as DayKey, 'to', v)
                                  }
                                />
                              </div>
                            </div>
                            {dayData.from &&
                              dayData.to &&
                              dayData.from >= dayData.to &&
                              !editData.operatingHours?.allDay && (
                                <p className="text-danger m-0 pt-1" style={{ fontSize: '12px' }}>
                                  {t('closeTimeGreaterThanOpenTime')}
                                </p>
                              )}
                          </div>
                        ) : (
                          <div className="day-closed">
                            <p className="closed-text">{t('closed')}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {isEdit && (
            <div className="footer-actions-edit">
              <MyButton label={t('cancel')} buttonType="outline" onClickFunc={toggleEditMode} />
              <MyButton
                label={t('save')}
                buttonType="submit"
                onClickFunc={handleSubmitStationDetails}
                loading={formStatus.buttonLoader}
                disableButton={formStatus.buttonLoader}
              />
            </div>
          )}
        </div>
      )}
      {/* ===================== DEVICE INFO (BOTTOM) ===================== */}
      {!isEdit && (
        <div className="table-container">
          <p className="heading">{t('deviceInfo')}</p>
          <div>
            {' '}
            <CustomTable
              serialNo
              header={column}
              data={chargingPointData}
              loading={false}
              filterAllow={false}
              sizeChangeButtonRequired={false}
              limit={0}
              currentPage={0}
              totalPages={0}
              totalResults={0}
              rowClickEvent={handleOpenDeviceSidePanel}
              rowClickParamsKey="deviceId"
              rowClassName={(row: ChargingPointProperties) =>
                row._id === activeDeviceId ? 'activeRow' : ''
              }
              topHeader={false}
              handlePageChange={() => {}}
              handleLimitChange={() => {}}
              leftTopContent={<p className="device-title">{t('deviceInfo')}</p>}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default StationSidePanel;
